import { FC } from 'react';
import { Loader } from '@new/common/ui/Loader/Loader';
import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { CourseHearingsProvider, useCourseHearingsContext } from '../context/CourseHearingsContext';
import { CourseHearingsTable } from '../components/CourseHearingsTable/CourseHearingsTable';
import { HearingFilters } from '../components/HearingFilters/HearingFilters';
import { CourseHearingDrawer } from '../components/CourseHearingDrawer/CourseHearingDrawer';
import { HearingDateFilter } from '../components/HearingDateFilter/HearingDateFilter';
import { SearchBar } from '@new/common/ui/SearchBar/SearchBar';
import { SEARCH_MODE_OPTIONS } from '../constants/courseHearings.constants';

const SEARCH_MODE_DROPDOWN_OPTIONS = SEARCH_MODE_OPTIONS.map((o) => ({ ...o }));

const CourseHearingsContent: FC = () => {
  const {
    isLoading,
    isError,
    selectedCall,
    setSelectedCall,
    searchQuery,
    setSearchQuery,
    searchMode,
    setSearchMode,
  } = useCourseHearingsContext();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-(--neutral-black-NB700) mb-4">Failed to load course hearings.</p>
          <button
            onClick={() => window.location.reload()}
            className="text-(--primary-O900) hover:underline font-medium text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex flex-col gap-8 p-6 lg:p-8">
        <PageHeader
          title="Course Hearings"
          description="Manage student hearing requests and schedules"
        />

        <section className="bg-(--neutral-white-NW1000) rounded-xl border border-(--neutral-white-NW800) shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 px-4 sm:px-6 py-4 border-b border-(--neutral-white-NW800)">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <SearchBar
                options={SEARCH_MODE_DROPDOWN_OPTIONS}
                value={searchMode}
                onChange={setSearchMode}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
              />
              <HearingDateFilter />
            </div>
            <HearingFilters />
          </div>

          <CourseHearingsTable />
        </section>
      </div>

      <CourseHearingDrawer selectedCall={selectedCall} setSelectedCall={setSelectedCall} />
    </div>
  );
};

export const CourseHearingsPage: FC = () => (
  <CourseHearingsProvider>
    <CourseHearingsContent />
  </CourseHearingsProvider>
);
